import { apiClient } from './client';

// Mirrors packages/api/src/modules/tenders/index.ts (reTenderDiary) — the web
// app keeps its own copy, as sourcing.ts and plays.ts do.

export type ChallengerBasis = 'lost_bid' | 'panel' | 'frequent_winner';

export const CHALLENGER_BASIS_LABELS: Record<ChallengerBasis, string> = {
  lost_bid: 'bid last time',
  panel: 'on the buyer’s panel',
  frequent_winner: 'wins similar contracts',
};

export const END_SOURCE_LABELS: Record<string, string> = {
  explicit: 'end date on the notice',
  derived_start: 'start + duration',
  derived_conclusion: 'signature + duration',
  derived_award: 'award + duration',
};

/** The diary sends only what the page shows: no country, and the id only for a company we track. */
export interface ReTenderParty {
  name: string;
  matchedCompanyId?: string | null;
}

export interface ReTenderChallenger extends ReTenderParty {
  basis: ChallengerBasis;
  /** Awards (frequent winner) or seats (panel); absent for a lost bid. */
  awards?: number | null;
}

export interface ReTenderContract {
  ref: string;
  title: string | null;
  buyerName: string | null;
  buyerCountry: string | null;
  cpvDivision: string | null;
  endDate: string;
  endSource: string | null;
  valueAmount: number | null;
  valueCurrency: string | null;
  isFramework: boolean;
  bidsReceived: number | null;
  noticeUrl: string | null;
  incumbents: ReTenderParty[];
  /** The strongest few (server: DIARY_CHALLENGERS_SHOWN), strongest basis first. */
  challengers: ReTenderChallenger[];
  /** Every challenger found; more than `challengers.length` when the list was cut. Absent on older builds. */
  challengerCount?: number;
}

export interface ReTenderDiaryMonth {
  month: string;
  label: string;
  contracts: ReTenderContract[];
}

export interface ReTenderDiary {
  from: string;
  to: string;
  totalContracts: number;
  capped: boolean;
  /** First month (YYYY-MM) the server did not read: it ran out of time. Null when complete. */
  incompleteFrom: string | null;
  incompleteReason: 'budget' | 'timeout' | null;
  /** Widest range one request may ask for, in months (the server refuses wider). */
  maxMonths: number;
  months: ReTenderDiaryMonth[];
  /** When the server computed this answer; it reuses one for up to 15 minutes. Absent on older builds. */
  cachedAt?: string;
}

/** Mirrors RE_TENDER_DIARY_MAX_MONTHS (api tenders/re-tender-bounds.ts). */
export const RE_TENDER_DIARY_MAX_MONTHS = 12;

export interface ReTenderDiaryParams {
  /** YYYY-MM. */
  from?: string;
  /** YYYY-MM. */
  to?: string;
  minValue?: number;
  /** Buyer country, alpha-2 or alpha-3. */
  country?: string;
}

export function getReTenderDiary(params: ReTenderDiaryParams): Promise<ReTenderDiary> {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);
  if (params.minValue !== undefined && params.minValue > 0) qs.set('minValue', String(params.minValue));
  if (params.country) qs.set('country', params.country);
  const q = qs.toString();
  return apiClient.get(`/tenders/re-tender-diary${q ? `?${q}` : ''}`);
}

/** Contracts ending in the next 90 days: the Sourcing tiles, worked out and cached (15 min) on the server. */
export interface ReTenderSummary {
  windowFrom: string;
  windowTo: string;
  contracts: number;
  eurValue: number;
  unvalued: number;
  otherCurrency: number;
  capped: boolean;
  cachedAt?: string;
  fromCache?: boolean;
}

export function getReTenderSummary(): Promise<ReTenderSummary> {
  return apiClient.get('/tenders/re-tender-summary');
}
